import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AdminUsers from "./AdminUsers.jsx";
import AdminFuelCategories from "./AdminFuelCategories.jsx";
import AdminScrapeTargets from "./AdminScrapeTargets.jsx";
import AdminDbStats from "./AdminDbStats.jsx";
import AdminAbcBatch from "./AdminAbcBatch.jsx";
import AdminCalculationRules from "./AdminCalculationRules.jsx";
import AdminDataQuality from "./AdminDataQuality.jsx";
import AdminActivities from "./AdminActivities.jsx";
import { fetchApplicationVersion } from "../api/client.js";

const TAB_CONFIG = [
  {
    id: "data-quality", label: "admin.tabs.dataQuality", route: "/admin/data-quality", component: AdminDataQuality,
  }, {
    id: "users",
    label: "admin.tabs.users",
    route: "/admin/users",
    component: AdminUsers,
  },
  {
    id: "fuel",
    label: "admin.tabs.fuel",
    route: "/admin/fuel-categories",
    component: AdminFuelCategories,
  },
  {
    id: "targets",
    label: "admin.tabs.targets",
    route: "/admin/scrape-targets",
    component: AdminScrapeTargets,
  },
  {
    id: "abc-batch",
    label: "admin.tabs.abcBatch",
    route: "/admin/abc-batch",
    component: AdminAbcBatch,
  },
  {
    id: "calculation-rules",
    label: "admin.tabs.rules",
    route: "/admin/calculation-rules",
    component: AdminCalculationRules,
  },
  {
    id: "db",
    label: "admin.tabs.database",
    route: "/admin/db-stats",
    component: AdminDbStats,
  },
  {
    id: "activities",
    label: "admin.tabs.activities",
    route: "/admin/activities",
    component: AdminActivities,
  },
];

const DEFAULT_TAB = "users";

export default function AdminPanel({ initialTab = DEFAULT_TAB }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const validInitial = TAB_CONFIG.some((tab) => tab.id === initialTab)
    ? initialTab
    : DEFAULT_TAB;

  const [activeTab, setActiveTab] = useState(validInitial);
  const [applicationVersion, setApplicationVersion] = useState("");

  const tabByRoute = useMemo(() => {
    const map = new Map();
    TAB_CONFIG.forEach((tab) => map.set(tab.route, tab.id));
    return map;
  }, []);

  useEffect(() => {
    fetchApplicationVersion()
      .then((data) => setApplicationVersion(data.version || ""))
      .catch(() => setApplicationVersion(""));
  }, []);

  useEffect(() => {
    const routeTab = tabByRoute.get(location.pathname);
    if (routeTab && routeTab !== activeTab) {
      setActiveTab(routeTab);
      return;
    }
    if (!routeTab && validInitial !== activeTab) {
      setActiveTab(validInitial);
    }
  }, [location.pathname, tabByRoute, validInitial, activeTab]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    const targetTab = TAB_CONFIG.find((tab) => tab.id === tabId);
    if (!targetTab) return;
    setActiveTab(tabId);
    if (location.pathname !== targetTab.route) {
      navigate(targetTab.route, { replace: false });
    }
  };

  const ActiveComponent =
    TAB_CONFIG.find((tab) => tab.id === activeTab)?.component ||
    TAB_CONFIG[0].component;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-white border rounded-xl shadow p-4">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h1 className="text-2xl font-semibold">{t("admin.title")}</h1>
          {applicationVersion && (
            <span className="text-sm text-gray-500">{t("admin.version", { version: applicationVersion })}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 border-b pb-2">
          {TAB_CONFIG.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-2 rounded-t-md text-sm font-medium transition ${
                  isActive
                    ? "bg-blue-600 text-white shadow"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                type="button"
              >
                {t(tab.label)}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <ActiveComponent />
      </div>
    </div>
  );
}
